/**
 * Typed API client.
 * Endpoints are typed against @bookquest/shared-types (auto-generated from openapi.yaml).
 */
import type { components } from '@bookquest/shared-types';

type Schemas = components['schemas'];
export type LibraryBook       = Schemas['LibraryBook'];
export type LibraryPreview    = Schemas['LibraryPreview'];
export type BookSummary       = Schemas['BookSummary'];
export type Book              = Schemas['Book'];
export type UploadResult      = Schemas['UploadResult'];
// MapView base from OpenAPI + 마인드맵 엔진 v2 확장 필드 (svg_urls, gamemap).
// OpenAPI yaml은 별도 재생성 PR에서 동기화. 백엔드 schemas.MapView가 진실.
export type MapView = Schemas['MapView'] & {
  svg_urls?: { full?: string; l0?: string; full_path?: string; l0_path?: string } | null;
  gamemap?: Record<string, unknown> | null;
};
export type Question          = Schemas['Question'];
export type AttemptResult     = Schemas['AttemptResult'];
export type Credential        = Schemas['Credential'];

// 인라인 리스트 응답 형태 (openapi.yaml 의 /library GET 응답)
export interface LibraryListView { items: LibraryBook[]; total: number }

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.body = body;
  }
}

/** Loose t-fn type so apiErrorMessage doesn't depend on the i18n key registry. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = (key: any) => string;

/**
 * Map any thrown error → user-facing localized message, with HTTP status branch.
 * Server's detail string wins when present; else fall back to status-tiered i18n key.
 *
 *   400 → api.error.badRequest    (request shape rejected)
 *   401 → api.error.unauthorized  (handler also signs user out)
 *   403 → api.error.forbidden     (you don't have access)
 *   404 → api.error.notFound      (resource missing / moved)
 *   413 → api.error.tooLarge      (file > limit)
 *   415 → api.error.unsupported   (e.g. magic-number rejection)
 *   429 → api.error.rateLimited
 *   5xx → api.error.server        (transient — retry suggested)
 *   net → api.error.network       (fetch failed entirely, e.g. offline)
 */
export function apiErrorMessage(err: unknown, t: TFn): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string } | null;
    if (body?.detail) return body.detail;
    if (err.status === 400) return t('api.error.badRequest');
    if (err.status === 401) return t('api.error.unauthorized');
    if (err.status === 403) return t('api.error.forbidden');
    if (err.status === 404) return t('api.error.notFound');
    if (err.status === 413) return t('api.error.tooLarge');
    if (err.status === 415) return t('api.error.unsupported');
    if (err.status === 429) return t('api.error.rateLimited');
    if (err.status >= 500)  return t('api.error.server');
    return `${t('api.error.unknown')} (${err.status})`;
  }
  if (err instanceof TypeError && /fetch|network/i.test(String(err.message))) {
    return t('api.error.network');
  }
  return t('api.error.unknown');
}

let jwt: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  jwt = token;
}

/**
 * Register a callback invoked whenever an authenticated request returns 401.
 * AuthProvider wires this to `supabase.auth.signOut()` so a server-side token
 * revocation, expiry, or signing-key rotation immediately drops the client
 * to /login instead of looping through failed requests.
 *
 * Only one handler is supported (the auth context owns auth state).
 */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (jwt) headers.set('Authorization', `Bearer ${jwt}`);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    // 401 = token rejected by server. Only fire the handler if we *had* a
    // token to begin with — anonymous endpoints (/library) returning 401 are
    // bugs to surface, not a reason to sign anyone out.
    if (res.status === 401 && jwt && unauthorizedHandler) {
      try { unauthorizedHandler(); } catch { /* never block on this */ }
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ============================================================================
// Library (public)
// ============================================================================
export const library = {
  list(params: { category?: string; grade_band?: string; featured?: boolean; limit?: number; offset?: number } = {}): Promise<LibraryListView> {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
    const qs = q.toString();
    return request(`/library${qs ? `?${qs}` : ''}`);
  },
  preview(bookId: string): Promise<LibraryPreview> {
    return request(`/library/${bookId}/preview`);
  },
};

// ============================================================================
// Books (auth required)
// ============================================================================
export const books = {
  list(source: 'all' | 'library' | 'upload' = 'all'): Promise<BookSummary[]> {
    return request(`/books?source=${source}`);
  },
  get(id: string): Promise<Book> {
    return request(`/books/${id}`);
  },
  async upload(pdf: File, country_iso?: string): Promise<UploadResult> {
    const fd = new FormData();
    fd.append('pdf', pdf);
    if (country_iso) fd.append('country_iso', country_iso);
    return request('/books', { method: 'POST', body: fd });
  },
};

// ============================================================================
// Maps + Questions + Attempts
// ============================================================================
export const maps = {
  byBook(bookId: string): Promise<MapView> {
    return request(`/maps/by-book/${bookId}`);
  },
};
export const questions = {
  byBook(bookId: string, tier?: string): Promise<{ items: Question[] }> {
    const q = tier ? `?tier=${tier}` : '';
    return request(`/questions/by-book/${bookId}${q}`);
  },
};
export const attempts = {
  submit(question_id: string, chosen: string, response_time_ms?: number): Promise<AttemptResult> {
    return request('/attempts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_id, chosen, response_time_ms }),
    });
  },
};

// ============================================================================
// Jobs — 분석/출제 파이프라인 진행 상태 (업로드 후 진행 페이지)
// ============================================================================
export interface JobView {
  id: string; book_id: string; status: string; progress: number;
  stage?: string | null; message?: string | null;
  total_cost_usd: number; total_calls: number; duration_sec: number;
  started_at?: string | null; completed_at?: string | null; created_at: string;
  error?: Record<string, unknown> | null;
}
export const jobs = {
  get(id: string): Promise<JobView> { return request(`/jobs/${id}`); },
};

// ============================================================================
// Credentials
// ============================================================================
export interface CredentialSummary {
  total: number; by_tier: Record<string, number>; books: number; highest_tier: string | null;
}
export const credentials = {
  me(tier?: string): Promise<{ items: Credential[] }> {
    const q = tier ? `?tier=${tier}` : '';
    return request(`/credentials/me${q}`);
  },
  summary(): Promise<CredentialSummary> { return request('/credentials/summary'); },
  verify(urn: string): Promise<CredentialVerifyResult> {
    return request(`/credentials/verify?urn=${encodeURIComponent(urn)}`);
  },
};
export interface CredentialVerifyResult {
  found: boolean; valid: boolean; tier?: string | null;
  credential_type?: string | null; issued_at?: string | null; status?: string | null;
}

// ============================================================================
// Achievement report (학습자 성취 리포트 — §18). 백엔드 build_report 가 진실.
// ============================================================================
export interface DomainScore {
  domain: string; attempted: number; score_sum: number; accuracy: number; level: string;
}
export interface TrendPoint {
  window: number; attempted: number; accuracy: number; theta: number | null;
}
export interface AchievementReport {
  user_id: string; book_id: string;
  total_attempts: number; total_score: number;
  overall_accuracy: number; overall_level: string; theta: number | null;
  naep: Record<string, DomainScore>;
  lidia: Record<string, DomainScore>;
  trend: TrendPoint[];
}

export const reports = {
  byBook(bookId: string): Promise<AchievementReport> {
    return request(`/reports/${bookId}`);
  },
};

// ============================================================================
// LARC public stats (무인증 — 검토 위원회 투명성)
// ============================================================================
export interface LarcPublicStats {
  approved: number; larc_pending: number; ai_reviewed: number; rejected: number;
  total_questions: number; reports_open: number; reports_resolved: number;
}
export const larc = {
  stats(): Promise<LarcPublicStats> { return request('/larc/public'); },
};

// ============================================================================
// Knowledge Vault (지식 창고)
// ============================================================================
export type VaultKind = 'memo' | 'quote' | 'insight' | 'feedback';
export interface VaultNote {
  id: string; user_id: string; book_id?: string | null;
  kind: VaultKind; content: string; tags: string[]; created_at?: string | null;
}
export interface VaultNoteInput {
  book_id?: string | null; kind?: VaultKind; content: string; tags?: string[];
}
export const vault = {
  list(params: { book_id?: string; kind?: string; q?: string; tag?: string } = {}): Promise<VaultNote[]> {
    const qp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qp.set(k, String(v));
    const s = qp.toString();
    return request(`/vault${s ? `?${s}` : ''}`);
  },
  create(input: VaultNoteInput): Promise<VaultNote> {
    return request('/vault', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
  },
  remove(id: string): Promise<void> {
    return request(`/vault/${id}`, { method: 'DELETE' });
  },
};

// ============================================================================
// Gamification (G5) — types inline (openapi.yaml 동기화는 별도 PR; 백엔드 schemas가 진실)
// ============================================================================
export interface GamLevel {
  level: number; total_xp: number; level_floor: number;
  next_level_at: number; into_level: number; needed: number; ratio: number;
}
export interface GamBadge {
  id: string; name_ko: string; desc: string; earned: boolean; earned_at?: string | null;
}
export interface GamSummary { user_id: string; level: GamLevel; badges: GamBadge[] }
export interface GamRule { kind: string; xp: number }
export interface GamRules { xp_rules: GamRule[]; badges: GamBadge[]; level_curve: number[] }
export interface GamLeaderRow { rank: number; user_id: string; value: number }
export interface GamLeaderboard {
  metric: string; period: string; rows: GamLeaderRow[]; my_rank: number | null;
}
export type GamPeriod = 'daily' | 'weekly' | 'monthly' | 'all';

export const gamification = {
  me(): Promise<GamSummary> { return request('/gamification/me'); },
  badges(): Promise<GamBadge[]> { return request('/gamification/badges'); },
  rules(): Promise<GamRules> { return request('/gamification/rules'); },
  leaderboard(period: GamPeriod = 'all'): Promise<GamLeaderboard> {
    return request(`/gamification/leaderboard?period=${period}`);
  },
};

// ============================================================================
// Question proposals (사용자 문제 제안) — types inline
// ============================================================================
export interface ProposalChoice { label: string; text: string }
export interface ProposalView {
  id: string; book_id: string; stem: string; item_format: string;
  tier?: string | null; category?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  review_notes?: string | null; adopted_question_id?: string | null;
  created_at?: string | null; decided_at?: string | null;
}
export interface ProposalCreateInput {
  book_id: string; stem: string; choices: ProposalChoice[]; correct_choice: string;
  item_format?: string; tier?: string | null; category?: string | null;
  explanation?: string | null;
}

export const proposals = {
  submit(input: ProposalCreateInput): Promise<ProposalView> {
    return request('/proposals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  },
  mine(bookId?: string): Promise<ProposalView[]> {
    const q = bookId ? `?book_id=${bookId}` : '';
    return request(`/proposals/me${q}`);
  },
};

// ============================================================================
// Admin console — types inline (백엔드 server/routes/admin·reviews·proposals 가 진실)
// ============================================================================
export interface AdminStats { library_books: number; user_uploads: number; paid_uploads: number }
export interface AdminLibraryItem {
  book_id: string; title?: string | null; category?: string | null;
  grade_band?: string | null; featured: boolean; sort_weight?: number | null;
  tags: string[]; rationale?: string | null;
}
export interface CurateInput {
  book_id: string; category?: string | null; grade_band?: string | null;
  sort_weight?: number; rationale?: string | null; tags?: string[]; featured?: boolean;
}
export interface PricingPolicy {
  id: string; name: string; description?: string | null;
  user_upload_price_usd?: number | null; dedupe_share_price_usd?: number | null;
  library_price_usd?: number | null; pricing_by_country?: Record<string, unknown> | null;
  subscription_includes_library?: boolean; subscription_monthly_uploads?: number | null;
  is_default?: boolean; is_active?: boolean;
  effective_from?: string | null; effective_until?: string | null;
}
export interface AdminCredentialItem {
  id: string; credential_urn: string; user_id: string; tier: string;
  credential_type: string; trigger_kind: string;
  issued_at?: string | null; expires_at?: string | null; anchored_at?: string | null;
}
export interface LarcQueueItem {
  id: string; book_id: string; question_code: string; tier: string;
  category?: string | null; item_format: string; stem: string;
  review_status: string; routing_reason?: string | null; review_confidence?: number | null;
}
export type ReviewVerdict = 'approved' | 'rejected' | 'revision_requested';
export interface ProposalQueueItem {
  id: string; book_id: string; stem: string; item_format: string;
  tier?: string | null; category?: string | null; status: string;
  proposer_id: string; choices: ProposalChoice[]; correct_choice: string;
  explanation?: string | null; created_at?: string | null;
}

function adminQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null) q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

// Phase 2 monitoring types
export interface AdminJobItem {
  id: string; book_id: string; status: string; progress: number;
  stage?: string | null; message?: string | null;
  total_cost_usd: number; total_calls: number; duration_sec: number;
  error?: Record<string, unknown> | null; degradations?: Record<string, unknown>[] | null;
  created_at?: string | null; started_at?: string | null; completed_at?: string | null;
}
export interface CostByModel {
  model: string; cost_usd: number; calls: number; input_tokens: number; output_tokens: number;
}
export interface CostByDay { day: string; cost_usd: number }
export interface AdminCostSummary {
  days: number; total_usd: number; total_calls: number;
  by_model: CostByModel[]; by_day: CostByDay[];
}
export interface AdminReportItem {
  id: string; question_id: string; user_id: string; reason: string;
  detail?: string | null; status: string; created_at?: string | null; resolved_at?: string | null;
}
// Phase 3 governance types
export interface AdminInstitutionItem {
  id: string; legal_name: string; display_name?: string | null; type_id: string;
  country_iso: string; license_type: string; license_seats: number; license_seats_used: number;
  license_expires_at?: string | null; status: string; lti_platform_id?: string | null;
}
export interface XpKindCount { kind: string; xp: number; count: number }
export interface TopXpUser { user_id: string; total_xp: number; events: number }
export interface BadgeCount { badge_id: string; count: number }
export interface AdminGamificationAudit {
  days: number; total_xp: number; total_events: number;
  by_kind: XpKindCount[]; top_users: TopXpUser[]; badges: BadgeCount[];
}
export interface AdminCertProfileItem {
  id: string; name: string; description?: string | null;
  is_default: boolean; is_active: boolean; is_system: boolean;
  scope_country_iso?: string | null; scope_institution_id?: string | null;
}
export interface AdminStandardItem {
  id: string; name: string; description?: string | null;
  primary_standard: string; score_report_format: string; is_default: boolean; is_system: boolean;
}
export interface AdminOpsReportRow {
  month: string; jobs_total: number; jobs_succeeded: number; jobs_failed: number; cost_usd: number;
}
export interface AdminOpsReport { months: number; rows: AdminOpsReportRow[] }

export const admin = {
  stats(): Promise<AdminStats> { return request('/admin/stats'); },

  jobs(status?: string, limit = 50): Promise<AdminJobItem[]> {
    return request(`/admin/jobs${adminQuery({ status, limit })}`);
  },
  cost(days = 30): Promise<AdminCostSummary> {
    return request(`/admin/cost${adminQuery({ days })}`);
  },
  reports(status = 'open', limit = 50): Promise<AdminReportItem[]> {
    return request(`/admin/reports${adminQuery({ status, limit })}`);
  },
  reportDecision(
    id: string, body: { action: 'resolve' | 'dismiss'; notes?: string | null },
  ): Promise<{ id: string; status: string }> {
    return request(`/admin/reports/${id}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  },

  // Phase 3 governance
  institutions(status?: string, limit = 100): Promise<AdminInstitutionItem[]> {
    return request(`/admin/institutions${adminQuery({ status, limit })}`);
  },
  setInstitutionStatus(id: string, status: string): Promise<AdminInstitutionItem> {
    return request(`/admin/institutions/${id}/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
  },
  gamificationAudit(days = 30, top = 20): Promise<AdminGamificationAudit> {
    return request(`/admin/gamification/audit${adminQuery({ days, top })}`);
  },
  certProfiles(): Promise<AdminCertProfileItem[]> { return request('/admin/cert-profiles'); },
  updateCertProfile(id: string, patch: Partial<AdminCertProfileItem>): Promise<AdminCertProfileItem> {
    return request(`/admin/cert-profiles/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
  },
  standards(): Promise<AdminStandardItem[]> { return request('/admin/standards'); },
  opsReport(months = 6): Promise<AdminOpsReport> {
    return request(`/admin/ops-report${adminQuery({ months })}`);
  },

  library(): Promise<AdminLibraryItem[]> { return request('/admin/library'); },
  curate(input: CurateInput): Promise<{ book_id: string; curated: boolean }> {
    return request('/admin/library/curate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
  },
  uncurate(bookId: string): Promise<void> {
    return request(`/admin/library/${bookId}`, { method: 'DELETE' });
  },

  pricing(activeOnly = false): Promise<PricingPolicy[]> {
    return request(`/admin/pricing${adminQuery({ active_only: activeOnly })}`);
  },
  updatePricing(id: string, patch: Partial<PricingPolicy>): Promise<PricingPolicy> {
    return request(`/admin/pricing/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
  },

  credentials(tier?: string): Promise<AdminCredentialItem[]> {
    return request(`/admin/credentials${adminQuery({ tier })}`);
  },
  revokeCredential(id: string, reason: string): Promise<unknown> {
    return request(`/credentials/${id}/revoke`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
  },

  reviewQueue(bookId?: string, limit = 50): Promise<LarcQueueItem[]> {
    return request(`/reviews/queue${adminQuery({ book_id: bookId, limit })}`);
  },
  reviewDecision(
    questionId: string, body: { verdict: ReviewVerdict; confidence?: number | null; notes?: string | null },
  ): Promise<{ question_id: string; review_status: string; review_id: string }> {
    return request(`/reviews/${questionId}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  },

  proposalQueue(bookId?: string, limit = 50): Promise<ProposalQueueItem[]> {
    return request(`/proposals/queue${adminQuery({ book_id: bookId, limit })}`);
  },
  proposalDecision(
    id: string, body: { verdict: 'accept' | 'reject'; notes?: string | null },
  ): Promise<{ id: string; status: string; adopted_question_id: string | null }> {
    return request(`/proposals/${id}/decision`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  },
};

export { ApiError };
